/**
 * glmLightningService.ts — ومضات البرق المرصودة من GOES GLM.
 *
 * يقرأ `lightning.json` الذي يكتبه `backend/scripts/glm_to_flashes.py`.
 *
 * الشكل مصفوفات متوازية (`lat[]`, `lon[]`, `t[]`) لا مصفوفة كائنات، وهذا ليس تفصيل
 * تنسيق: عشرة آلاف كائن `{lat,lon,t}` تكرّر أسماء المفاتيح عشرة آلاف مرّة فتضاعف
 * الحجم، وتُنشئ عشرة آلاف كائن في ذاكرة العميل عند التحليل. المصفوفات المتوازية
 * تُحلَّل إلى ثلاث مصفوفات أرقام تقرؤها حلقة الرسم مباشرةً.
 *
 * و`t` **عمرٌ بالثواني قبل `generatedAt`** لا طابعٌ مطلق: الرسم يحتاج العمر ليخفت
 * به، فتخزينه جاهزاً يوفّر طرحاً لكل ومضة في كل إطار، ويجعل الملفّ أصغر (رقم من
 * ثلاث خانات بدل عشر).
 */

export interface LightningWindow {
    /** لحظة الإسناد — كل الأعمار تُقاس منها. */
    generatedAt: string;
    /** طول النافذة بالدقائق. */
    windowMinutes: number;
    count: number;
    lat: number[];
    lon: number[];
    /** عمر الومضة بالثواني قبل `generatedAt`. */
    t: number[];
    /**
     * ‏1 إن كانت الومضة داخل نطاق تغطية رادارٍ ما، 0 إن لم تكن.
     *
     * يكتبه المولّد (`radar_coverage.mask_rows`) لأنه وحده يملك أقنعة التغطية. وطبقة
     * الرادار تعرض ذوات `1` فقط — برقٌ بلا مطرٍ تحته يوحي بأن الرادار ناقص — بينما
     * طبقة صور الأقمار تعرض الكلّ: القمر يرى الجزيرة العربية وأفريقيا ولا رادار فيهما.
     *
     * ملفّ قديم بلا هذا الحقل: يُعامَل كأن كل ومضاته مغطّاة، فتُعرض الطبقة ناقصة
     * الإخفاء لا فارغة.
     */
    r?: number[];
    satellites: string[];
    source: string;
}

const DIR = 'rasters/';
/**
 * مصدران مرصودان بنفس الشكل تماماً، يكتب كلاًّ منهما مولّدٌ مستقلّ:
 *   • `lightning.json`     — NOAA GOES GLM: الأمريكتان.
 *   • `lightning-mtg.json` — EUMETSAT MTG-I1 LI: أوروبا، أفريقيا، الشرق الأوسط.
 * تغطيتاهما متجاورتان لا متراكبتان إلا على الأطلسي وشرق البرازيل، فالدمج يعطي معظم
 * الكرة المأهولة بالعواصف في طبقةٍ واحدة بلا مفتاحٍ ثانٍ يحتار المستخدم بينهما.
 */
const FILES = ['lightning.json', 'lightning-mtg.json'] as const;

/**
 * وتيرة السؤال. المولّد يعمل كل دقيقتين، فسؤالٌ كل ٩٠ ثانية يضمن ألّا يتأخّر العرض
 * دورةً كاملة، وبلا إغراق: الملفّ ~160 ك.ب ويضغطه nginx إلى عشرها تقريباً.
 */
export const LIGHTNING_POLL_MS = 90_000;

/**
 * أقصى عمر يُرسم. أقصر من نافذة المولّد عمداً: الومضة عند حافّة النافذة تكون قد
 * خفتت إلى العدم أصلاً، ورسمها يكلّف دورةً في الحلقة بلا بكسل مرئي.
 */
export const MAX_AGE_S = 1200;

async function fetchOne(file: string): Promise<LightningWindow | null> {
    try {
        const res = await fetch(`${import.meta.env.BASE_URL}${DIR}${file}`, { cache: 'no-cache' });
        if (!res.ok) return null;
        const data = (await res.json()) as LightningWindow;
        if (!Array.isArray(data?.lat) || !Array.isArray(data?.lon) || !Array.isArray(data?.t)) {
            return null;
        }
        if (data.lat.length !== data.lon.length || data.lat.length !== data.t.length) return null;
        if (Array.isArray(data.r) && data.r.length !== data.t.length) delete data.r;
        if (!Number.isFinite(Date.parse(data.generatedAt))) return null;
        return data;
    } catch {
        return null;
    }
}

/**
 * يجلب المصدرين معاً ويدمجهما في نافذة واحدة.
 *
 * كلٌّ منهما يقيس `t` من **لحظة توليده هو**، واللحظتان تختلفان بثوانٍ أو دقائق
 * (مولّدان مستقلّان). فالدمج الصحيح يُعيد إسناد الأقدم إلى لحظة الأحدث: عمرٌ + فرق
 * اللحظتين. ولصق المصفوفات كما هي كان سيجعل ومضات أحد المصدرين أحدث ممّا هي فعلاً.
 *
 * وفشل أحدهما (مفتاحٌ منتهٍ، ملفّ غائب) لا يُسقط الآخر — يُعاد ما وصل.
 */
export async function fetchLightning(): Promise<LightningWindow | null> {
    const wins = (await Promise.all(FILES.map(fetchOne))).filter((w): w is LightningWindow => w !== null);
    if (wins.length === 0) return null;
    if (wins.length === 1) return wins[0];

    const genMs = wins.map((w) => Date.parse(w.generatedAt));
    const latestMs = Math.max(...genMs);
    const lat: number[] = [];
    const lon: number[] = [];
    const t: number[] = [];
    const r: number[] = [];
    wins.forEach((w, k) => {
        const shift = Math.round((latestMs - genMs[k]) / 1000);
        for (let i = 0; i < w.t.length; i++) {
            lat.push(w.lat[i]);
            lon.push(w.lon[i]);
            t.push(w.t[i] + shift);
            r.push(w.r ? w.r[i] : 1);
        }
    });
    return {
        generatedAt: new Date(latestMs).toISOString(),
        windowMinutes: Math.max(...wins.map((w) => w.windowMinutes)),
        count: t.length,
        lat, lon, t, r,
        satellites: wins.flatMap((w) => w.satellites ?? []),
        source: wins.map((w) => w.source).join(' + '),
    };
}

/**
 * العمر الحقيقي للومضة الآن = عمرها المخزَّن + ما مضى منذ توليد الملفّ.
 *
 * بلا الحدّ الثاني تتجمّد الأعمار بين دورتين، فتبقى الومضات ساطعةً كما هي دقيقتين
 * ثم تقفز جميعاً دفعةً واحدة عند وصول الملفّ التالي — ومضةٌ جماعية لا تشبه شيئاً
 * في الطبيعة. بإضافته تخفت كل ومضة انسيابياً وتختفي في وقتها الصحيح.
 */
export function ageSeconds(win: LightningWindow, index: number, nowMs: number): number {
    const base = Date.parse(win.generatedAt);
    const elapsed = Number.isFinite(base) ? Math.max(0, (nowMs - base) / 1000) : 0;
    return win.t[index] + elapsed;
}

/**
 * ── ساعات البرق الأقدم من النافذة الحيّة ──────────────────────────────────────
 *
 * النافذة الحيّة ساعتان، وشريط القمر الصناعي ~5.5 ساعات: فأوّله كان بلا ومضة.
 * `backend/scripts/lightning_hours.py` يكتب ملفّاً لكل ساعة، ويُطلب هنا فقط حين
 * يصل المؤشّر إليها. الساعة المكتملة لا تتغيّر، فتُخزَّن في الذاكرة طوال الجلسة.
 */
export interface LightningHour {
    /** رأس الساعة (ثوانٍ). */
    hour: number;
    lat: number[];
    lon: number[];
    /** ثوانٍ منذ رأس الساعة. */
    t: number[];
    r: number[];
}

interface HourIndex { files: { src: string; hour: number; file: string }[] }

const HOURS_DIR = `${DIR}lightning-hours/`;
const hourCache = new Map<string, Promise<LightningHour | null>>();
let indexAt = 0;
let indexP: Promise<HourIndex | null> | null = null;

function hourIndex(): Promise<HourIndex | null> {
    // الفهرس يتغيّر كل دقيقتين؛ يكفي تجديده مع استطلاع البرق نفسه.
    if (!indexP || Date.now() - indexAt > LIGHTNING_POLL_MS) {
        indexAt = Date.now();
        indexP = fetch(`${import.meta.env.BASE_URL}${HOURS_DIR}index.json`, { cache: 'no-cache' })
            .then((r) => (r.ok ? (r.json() as Promise<HourIndex>) : null))
            .catch(() => null);
    }
    return indexP;
}

/** ملفّات الساعات التي تتقاطع مع [from, to) (ثوانٍ)، من المصدرين معاً. */
export async function fetchLightningHours(from: number, to: number): Promise<LightningHour[]> {
    const idx = await hourIndex();
    if (!idx) return [];
    const want = idx.files.filter((f) => f.hour + 3600 > from && f.hour < to);
    const got = await Promise.all(want.map((f) => {
        let p = hourCache.get(f.file);
        if (!p) {
            p = fetch(`${import.meta.env.BASE_URL}${HOURS_DIR}${f.file}`)
                .then((r) => (r.ok ? (r.json() as Promise<LightningHour>) : null))
                .catch(() => null);
            hourCache.set(f.file, p);
            // الفشل لا يُخزَّن: محاولةٌ لاحقة قد تنجح.
            void p.then((v) => { if (!v) hourCache.delete(f.file); });
        }
        return p;
    }));
    return got.filter((h): h is LightningHour => h !== null && Array.isArray(h.t));
}
